import React, { useState } from 'react';
import { Play, Download, ArrowUpCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/export';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  btn: { fontFamily: 'DM Sans, sans-serif', background: 'var(--primary)', color: 'var(--bg)', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnOutline: { fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: 'var(--secondary)', border: '1px solid var(--secondary)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 },
  actions: { display: 'flex', gap: 12, marginBottom: '1.5rem', alignItems: 'center' },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.1)', color: 'var(--secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.05)', color: 'var(--text)', fontSize: '0.88rem' },
  spinner: { display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  sortBtn: { background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontSize: '0.7rem', marginLeft: 4 },
};

const fallbackData = [
  { name: 'Maria L.', currentProduct: 'Regular Latte', upgrade: 'Oat Milk Latte', priceDiff: '+$1.50', acceptProb: 0.78, reason: 'Ordered oat milk 2x in last month' },
  { name: 'Jake T.', currentProduct: 'Medium Cold Brew', upgrade: 'Large Cold Brew', priceDiff: '+$1.00', acceptProb: 0.82, reason: 'Consistently finishes medium quickly' },
  { name: 'Priya S.', currentProduct: 'House Drip', upgrade: 'Single Origin Pour Over', priceDiff: '+$2.50', acceptProb: 0.65, reason: 'Asked about origins at counter' },
  { name: 'Carlos R.', currentProduct: 'Iced Americano', upgrade: 'Nitro Cold Brew', priceDiff: '+$1.75', acceptProb: 0.72, reason: 'Flavor profile alignment' },
  { name: 'Emma W.', currentProduct: 'Small Chai', upgrade: 'Large Dirty Chai', priceDiff: '+$2.25', acceptProb: 0.68, reason: 'Espresso add-on pattern' },
  { name: 'Liam K.', currentProduct: 'Regular Cappuccino', upgrade: 'Cortado + Pastry Bundle', priceDiff: '+$3.00', acceptProb: 0.61, reason: 'Often browses pastry case' },
  { name: 'Aisha B.', currentProduct: 'Iced Tea', upgrade: 'Sparkling Tea Infusion', priceDiff: '+$1.25', acceptProb: 0.74, reason: 'Preference for premium beverages' },
  { name: 'Noah D.', currentProduct: 'Batch Brew', upgrade: 'Chemex Pour Over', priceDiff: '+$3.50', acceptProb: 0.55, reason: 'Weekend ritual potential' },
];

export default function UpsellRecommendation() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('acceptProb');
  const [sortDir, setSortDir] = useState('desc');

  const runModel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/models/upsell');
      setResults(res.data?.results || fallbackData);
      toast.success('Upsell model complete');
    } catch {
      setResults(fallbackData);
      toast.success('Upsell model complete (demo data)');
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...(results || [])].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
  const toggleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <h1 style={s.h1}>Upsell Recommendation</h1>
      <p style={s.desc}>
        Identifies product upgrade opportunities for each customer based on purchase history,
        taste profile, and behavioral signals. Focuses on natural next-step upgrades.
      </p>

      <div style={s.actions}>
        <button style={s.btn} onClick={runModel} disabled={loading}>
          {loading ? <><span style={s.spinner} /> Running...</> : <><Play size={16} /> Run Model</>}
        </button>
        {results && (
          <button style={s.btnOutline} onClick={() => exportToCSV(results, 'upsell_recommendations')}>
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {results && (
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                {[['name','Customer'],['currentProduct','Current'],['upgrade','Upgrade To'],['priceDiff','Price Diff'],['acceptProb','Accept Prob.'],['reason','Reason']].map(([k,l]) => (
                  <th key={k} style={s.th}>{l}<button style={s.sortBtn} onClick={() => toggleSort(k)}>{sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i}>
                  <td style={s.td}>{row.name}</td>
                  <td style={s.td}>{row.currentProduct}</td>
                  <td style={{ ...s.td, color: 'var(--primary)', fontWeight: 500 }}>{row.upgrade}</td>
                  <td style={s.td}>{row.priceDiff}</td>
                  <td style={s.td}>{(row.acceptProb * 100).toFixed(0)}%</td>
                  <td style={s.td}>{row.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
